/* ============================================================
   <Logo> — Route and Route brand mark
   Single source of truth in @rr/ui. Apps consume this component;
   they never re-import or re-tint the asset directly.

   The official white-on-cobalt SVG is identical in both themes.
   Every app uses the same source asset without per-app tinting.

   For an app that needs a label (e.g. the admin panel), use
   <LogoLockup label="ADMIN" />.
   ============================================================ */

import * as React from 'react'
import { cn } from '../../utils'
// Assets resolve to a plain URL string under Vite (tenant-web, rr-admin)
// but to a `{ src }` object (StaticImageData) under Next.js (books, portals,
// freight). Normalise both to a string so the same component works everywhere.
import logoAsset from '../../brand/logo.svg'

const toSrc = (a: unknown): string =>
  typeof a === 'string' ? a : (a as { src: string }).src

const LOGO_SRC = toSrc(logoAsset)

/** Kept for compatibility; the official badge is the same in both themes. */
export type LogoTone = 'primary' | 'mono-dark' | 'mono-light' | 'current'

type LogoProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** @deprecated The official badge has one colorway in both themes. */
  tone?: LogoTone
  /** Visual size in px. Defaults to 32 (matches topbar). */
  size?: number
  /** Accessible label. Defaults to "Route and Route". */
  label?: string
}

export function Logo({
  size = 32,
  label = 'Route and Route',
  className,
  tone: _tone,
  ...props
}: LogoProps) {
  return (
    <span
      role="img"
      aria-label={label}
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      {...props}
    >
      <img
        src={LOGO_SRC}
        alt=""
        width={size}
        height={size}
        className="h-full w-full object-contain"
        draggable={false}
      />
    </span>
  )
}

/** Lockup: mark + wordmark (or an app label like "ADMIN"), for topbars. */
export function LogoLockup({
  size = 28,
  className,
  brandText = 'Route and Route',
  /** Optional app tag rendered as a chip after the mark (e.g. "ADMIN"). */
  label,
  /** Hide the wordmark and show only the mark (+ optional label). */
  markOnly = false,
}: {
  tone?: LogoTone
  size?: number
  className?: string
  brandText?: string
  label?: string
  markOnly?: boolean
}) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-semibold', className)}>
      <Logo size={size} />
      {!markOnly && <span className="tracking-tight">{brandText}</span>}
      {label && (
        <span className="rounded-[6px] bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      )}
    </span>
  )
}
